/* ============================================================
   PALAMANO — Sección de pasos

   El cambio de paso lo hace el CSS con los radios. Aquí solo se
   añade lo que el CSS no puede: cambiar de paso al pasar el ratón
   por una miniatura, y que los vídeos no se detengan nunca.
   ============================================================ */
(() => {
  class PmnPasos extends HTMLElement {
    connectedCallback() {
      this.addEventListener('pointerenter', this.alPasarElRaton.bind(this), true);
      this.addEventListener('change', () => this.reproducir());
      this.reproducir();

      /* En celular la media y el paso llenan juntos la pantalla. Cuánto le
         toca a cada uno depende del texto, así que se mide. */
      this.medirHueco();
      window.addEventListener('resize', () => this.medirHueco());

      if ('ResizeObserver' in window) {
        const observador = new ResizeObserver(() => this.medirHueco());
        this.querySelectorAll('.pmn-pasos__antetitulo, .pmn-pasos__paso').forEach((e) => observador.observe(e));
      }
    }

    medirHueco() {
      const media = this.querySelector('.pmn-pasos__media');
      if (!media) return;

      if (!window.matchMedia('(max-width: 999px)').matches) {
        this.style.removeProperty('--pmn-pasos-hueco');
        return;
      }

      const trozos = [...this.querySelectorAll('.pmn-pasos__antetitulo, .pmn-pasos__paso')];
      const alto = trozos.reduce((suma, e) => suma + e.getBoundingClientRect().height, 0);

      /* El margen que separa la media del texto y un respiro abajo, para que
         las miniaturas no queden pegadas al borde de la pantalla. */
      const aire = parseFloat(getComputedStyle(media).marginBlockEnd || 0) + 32;

      this.style.setProperty('--pmn-pasos-hueco', `${Math.round(alto + aire)}px`);
    }

    alPasarElRaton(evento) {
      /* Solo con ratón: en una pantalla táctil el «hover» se dispara
         con el toque y se adelantaría al clic. */
      if (!window.matchMedia('(pointer: fine)').matches) return;

      const mini = evento.target.closest?.('.pmn-pasos__mini');
      if (!mini) return;

      const radio = this.querySelector(`#${CSS.escape(mini.getAttribute('for'))}`);
      if (radio && !radio.checked) {
        radio.checked = true;
        this.reproducir();
      }
    }

    /* El vídeo del paso visible se reproduce; el resto se pausa para
       no gastar datos ni batería de balde.

       Aquí hubo un IntersectionObserver que pausaba la sección fuera de
       pantalla. Se quitó: sólo tapaba el fallo de carga de arriba, y un
       observador que no dispara deja el vídeo parado para siempre. El
       arranque lo hace el `autoplay` del propio vídeo. */
    reproducir() {
      this.querySelectorAll('.pmn-pasos__media-item').forEach((item) => {
        const video = item.querySelector('video');
        if (!video) return;

        video.muted = true;
        video.loop = true;
        video.playsInline = true;

        /* Aquí estaba el fallo de verdad: el vídeo salía con `currentSrc`
           vacío y `readyState` 0 — es decir, el navegador nunca había
           elegido el fichero del `<source>`, así que `play()` se aceptaba
           pero no había nada que reproducir. (El de la galería, con el
           mismo fichero, sí lo tenía elegido.) El algoritmo de selección
           del recurso sólo se vuelve a lanzar con `load()`. */
        if (!video.currentSrc && video.querySelector('source')) {
          video.preload = 'auto';
          video.load();
        }

        const visible = getComputedStyle(item).visibility !== 'hidden';

        /* No se mira `document.hidden`: el navegador ya pausa y reanuda por
           su cuenta los vídeos de una pestaña que no se está viendo, y
           hacerlo también aquí los dejaba parados para siempre. */
        if (visible) {
          /* El navegador rechaza la primera petición si en ese instante aún
             considera que el vídeo no se está viendo, y lo hace devolviendo
             una promesa rechazada: si se ignora, el vídeo se queda parado
             para siempre. Por eso se reintenta unas cuantas veces. */
          const arrancar = (intento = 0) => {
            video.play().catch(() => {
              if (intento < 4) setTimeout(() => arrancar(intento + 1), 350);
            });
          };

          if (video.readyState >= 2) arrancar();
          else video.addEventListener('loadeddata', () => arrancar(), { once: true });
        } else {
          video.pause();
        }
      });
    }
  }

  if (!window.customElements.get('pmn-pasos')) {
    window.customElements.define('pmn-pasos', PmnPasos);
  }
})();
